/* eslint-disable react/jsx-sort-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  ChartEmpty,
  CHART_HEIGHT_RANKING,
  getProdutividadeChartLayout,
  getProdutividadeSeriesLabel,
  itemsHaveMediaDiaUtil,
  mapProdutividadeToDataset,
  PRODUTIVIDADE_INLINE_MAX_HEIGHT,
  PRODUTIVIDADE_ROW_HEIGHT,
} from './chartShared';

const formatTooltipValue = (row) => {
  if (!row) return '';
  if (row.useMedia) {
    const media = row.mediaDiaUtil ?? row.value;
    const estimado = row.diasUteisEstimado ? ' (dias úteis estimados)' : '';
    return `${media}/dia — ${row.total} em ${row.diasUteis} dias úteis${estimado}`;
  }
  return `${row.total} serviços no período`;
};

const ChartFootnotes = ({ useMedia }) => (
  <>
    {!useMedia && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Média por dia útil indisponível — exibindo total de serviços no período.
      </Typography>
    )}
    {useMedia && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Média = total digitado ÷ dias úteis (seg–sex, exceto feriados do município do digitador).
      </Typography>
    )}
  </>
);

ChartFootnotes.propTypes = {
  useMedia: PropTypes.bool.isRequired,
};

const chartWrapperSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  '& .MuiChartsLegend-root': { display: 'none' },
  '& .MuiChartsAxis-root': { '& text': { textAnchor: 'start' } },
};

/** Barras horizontais; inline com altura máxima e scroll para não empurrar a tabela. */
const ProdutividadeChart = ({ items, loading, height }) => {
  const theme = useTheme();
  const isExpanded = height != null;

  const dataset = useMemo(() => mapProdutividadeToDataset(items), [items]);
  const useMedia = itemsHaveMediaDiaUtil(items);
  const seriesLabel = useMemo(() => getProdutividadeSeriesLabel(items), [items]);
  const chartLayout = useMemo(() => getProdutividadeChartLayout(dataset), [dataset]);
  const scrollableHeight = Math.max(
    CHART_HEIGHT_RANKING,
    dataset.length * PRODUTIVIDADE_ROW_HEIGHT + 48,
  );
  const chartHeight = isExpanded ? (height || scrollableHeight) : scrollableHeight;
  const hasData = dataset.length > 0;

  const valueFormatter = useMemo(
    () => (value, context) => {
      const row = dataset[context?.dataIndex];
      return formatTooltipValue(row) || String(value);
    },
    [dataset],
  );

  const chart = (
    <BarChart
      layout="horizontal"
      dataset={dataset}
      series={[
        {
          dataKey: 'value',
          label: seriesLabel,
          color: theme.palette.primary.main,
          valueFormatter,
        },
      ]}
      yAxis={[
        {
          scaleType: 'band',
          dataKey: 'label',
          width: chartLayout.yAxisWidth,
          tickLabelStyle: {
            fontSize: 12,
            textAnchor: 'start',
          },
        },
      ]}
      xAxis={[{ min: 0 }]}
      height={chartHeight}
      margin={chartLayout.margin}
      slotProps={{ legend: { hidden: true } }}
      sx={{ width: '100%', maxWidth: '100%', ml: 0 }}
    />
  );

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={isExpanded ? chartHeight : PRODUTIVIDADE_INLINE_MAX_HEIGHT}
      />
    );
  }

  if (!hasData) {
    return <ChartEmpty message="Sem dados no período selecionado" />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {isExpanded ? (
        <Box sx={chartWrapperSx}>{chart}</Box>
      ) : (
        <Box
          sx={{
            ...chartWrapperSx,
            maxHeight: PRODUTIVIDADE_INLINE_MAX_HEIGHT,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {chart}
        </Box>
      )}
      <ChartFootnotes useMedia={useMedia} />
    </Box>
  );
};

ProdutividadeChart.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  height: PropTypes.number,
};

ProdutividadeChart.defaultProps = {
  items: [],
  loading: false,
  height: null,
};

export default ProdutividadeChart;
